/**
 * Wave 3.46 — Searchable Customer combobox.
 *
 * Why not a plain Shadcn Select?
 * — Customer lists can grow into the hundreds and we want type-ahead
 *   search + an inline "+ Add new" affordance without leaving the parent
 *   dialog (e.g. New Order Form).
 *
 * Implementation: Popover + filtered scrollable list. Keeps the
 * dependency surface small (no extra packages) and matches the look
 * of other dialog controls.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ChevronsUpDown, Check, Plus, X, User, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CustomerFormDialog } from './CustomerFormDialog';

export function CustomerCombobox({
  customers = [],
  value,                  // selected customer_id ('' / null = none)
  onChange,               // (newCustomerId, customerObject|null) => void
  companyId,
  onCustomerCreated,      // (customer) => void  — fired AFTER refresh
  placeholder = 'Search or add a customer…',
  allowClear = true,
  loading = false,
  testIdPrefix = 'customer-combobox',
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [addDlgOpen, setAddDlgOpen] = useState(false);
  const triggerRef = useRef(null);

  const selected = useMemo(
    () => customers.find((c) => c.id === value) || null,
    [customers, value],
  );

  const filtered = useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((c) => {
      const hay = [
        c.name || '',
        c.phone || '',
        c.email || '',
      ].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [customers, query]);

  // Highlight exact-match potential add candidate.
  const trimmed = (query || '').trim();
  const hasExactMatch = trimmed && customers.some(
    (c) => (c.name || '').toLowerCase() === trimmed.toLowerCase(),
  );

  useEffect(() => { if (!open) setQuery(''); }, [open]);

  const pick = (c) => {
    onChange(c?.id || '', c || null);
    setOpen(false);
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange('', null);
  };

  return (
    <>
      <Popover open={open && !disabled} onOpenChange={(v) => !disabled && setOpen(v)}>
        <PopoverTrigger asChild>
          <Button
            ref={triggerRef}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              'w-full justify-between font-normal h-9',
              !selected && 'text-slate-500',
            )}
            data-testid={`${testIdPrefix}-trigger`}
          >
            <span className="flex items-center gap-2 min-w-0 flex-1">
              <User size={14} className="text-slate-400 flex-shrink-0" />
              <span className="truncate">
                {selected ? selected.name : placeholder}
              </span>
            </span>
            <span className="flex items-center gap-1 flex-shrink-0">
              {selected && allowClear && (
                <X
                  size={14}
                  className="text-slate-400 hover:text-rose-600 cursor-pointer"
                  onClick={clear}
                  data-testid={`${testIdPrefix}-clear`}
                />
              )}
              <ChevronsUpDown size={14} className="text-slate-400" />
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0 w-[var(--radix-popover-trigger-width)] max-w-[420px]"
          align="start"
        >
          <div className="border-b border-[var(--divider)] p-2">
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, phone, email…"
              className="h-8 text-sm"
              data-testid={`${testIdPrefix}-search-input`}
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {loading ? (
              <div className="flex items-center justify-center py-6 text-xs text-slate-500">
                <Loader2 size={14} className="animate-spin mr-2" /> Loading…
              </div>
            ) : (
              <>
                {/* Wave 3.70 — Explicit "All Customers" option so operators can
                    clear a customer filter without hunting for the tiny X icon
                    on the trigger. Rendered only when the combobox exposes
                    clearing (allowClear) and there's actually a value to clear
                    OR when there are customers to filter (i.e. this is a
                    filter dropdown rather than a mandatory picker). */}
                {allowClear && (
                  <button
                    type="button"
                    onClick={() => pick(null)}
                    className={cn(
                      'w-full flex items-start gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 transition-colors border-b border-[var(--divider)]',
                      !value && 'bg-[var(--jewellery-bg)]/40',
                    )}
                    data-testid={`${testIdPrefix}-option-all`}
                  >
                    <Check
                      size={14}
                      className={cn(
                        'mt-1 flex-shrink-0',
                        !value ? 'text-[var(--jewellery-icon)]' : 'invisible',
                      )}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-[var(--ink-navy)] italic">
                        All customers
                      </div>
                      <div className="text-[10px] text-slate-500">
                        Show orders across every customer
                      </div>
                    </div>
                  </button>
                )}
                {filtered.length === 0 ? (
                  <div className="px-3 py-4 text-center text-xs text-slate-500">
                    {customers.length === 0
                      ? 'No customers yet. Use "+ Add" below to create one.'
                      : 'No matches. Refine the search or add a new customer.'}
                  </div>
                ) : (
                  filtered.slice(0, 200).map((c) => {
                    const isSel = c.id === value;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => pick(c)}
                        className={cn(
                          'w-full flex items-start gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 transition-colors',
                          isSel && 'bg-[var(--jewellery-bg)]/40',
                        )}
                        data-testid={`${testIdPrefix}-option-${c.id}`}
                      >
                        <Check
                          size={14}
                          className={cn(
                            'mt-1 flex-shrink-0',
                            isSel ? 'text-[var(--jewellery-icon)]' : 'invisible',
                          )}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-[var(--ink-navy)] truncate">
                            {c.name}
                          </div>
                          {(c.phone || c.email) && (
                            <div className="text-[10px] text-slate-500 truncate">
                              {[c.phone, c.email].filter(Boolean).join(' · ')}
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })
                )}
              </>
            )}
          </div>
          <div className="border-t border-[var(--divider)] p-2 bg-slate-50/60">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => { setOpen(false); setAddDlgOpen(true); }}
              className="w-full justify-start text-[var(--ink-navy)] hover:bg-white"
              disabled={hasExactMatch}
              data-testid={`${testIdPrefix}-add-new-button`}
            >
              <Plus size={13} className="mr-1.5" />
              {trimmed
                ? hasExactMatch
                  ? `"${trimmed}" already exists`
                  : `Add new customer "${trimmed}"`
                : 'Add new customer'}
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <CustomerFormDialog
        open={addDlgOpen}
        onOpenChange={setAddDlgOpen}
        companyId={companyId}
        defaultName={trimmed}
        onCreated={async (created) => {
          // Refresh parent list and auto-select the new customer.
          if (onCustomerCreated) await onCustomerCreated(created);
          if (created?.id) onChange(created.id, created);
        }}
      />
    </>
  );
}

export default CustomerCombobox;
